import { computed, reactive, ref } from "vue";
import { defineStore } from "pinia";
import type { LoadingStatus } from "./loading_status";
import StoragePolicyService from "@/core/services/StoragePolicyService";
import { ElMessage } from "element-plus";

export interface RedundancyScheme {
  scheme: 'rlnc' | 'replication'
}
export interface ECScheme extends RedundancyScheme {
  redundant_packets: number
}
export interface ReplicationScheme extends RedundancyScheme {
  replicas: number
}

export interface EncryptionScheme {
  scheme: 'aes'
}
export interface AesScheme extends EncryptionScheme {
  block_mode: string
  key_size: number
}

export interface DegradedWriteScheme {
  enabled: boolean
}

export interface StoragePolicyBase {
  name: string
  description?: string
  backends: number[] // Cloud storage backend IDs
  edge_devices: number[] // Edge Device IDs
  redundancy: RedundancyScheme
  encryption: EncryptionScheme | null
  degraded_writes?: DegradedWriteScheme
}

export interface StoragePolicy extends StoragePolicyBase {
  id: number
  is_default?: boolean
  buckets_num?: number
}

export interface AttachedStoragePolicy extends StoragePolicyBase {
  id: number
  s3_bucket_name: string
  bucket_ids: number[]
}


// Sanitize StoragePolicy or ArrachedStoragePolcy object received from the backend
export const init_storage_policy = (policy: any) : any => {
  // Set the cloud and edge IDs to an empty array if they are not set
  policy.backends = policy.backends || []
  // Edge device IDs are listed as 'edge_devices' in Storage Policies and 'edge_device_ids' in Attached Storage Policies
  policy.edge_devices = policy.edge_devices || policy.edge_device_ids || []
  // Delete policy.encryption if it's '{scheme='none'}'
  if(policy.encryption && policy.encryption.scheme.toLowerCase() == 'none'){
    delete policy.encryption
  }
  // Make the redundancy scheme lowercase
  policy.redundancy.scheme = policy.redundancy.scheme.toLowerCase()
  return policy
}

// Selected policy for the Storage Policy Details modal
export const selected_policy = ref<StoragePolicy | null>(null)

export const useStoragePolicyStore = defineStore("storage-policy", () => {
  const policies = reactive<StoragePolicy[]>([]);
  const policies_loading = reactive<LoadingStatus>({loading: false})

  const default_policy = computed<StoragePolicy|undefined>(_ => policies.find(p => p.is_default))

  const attached_policies = reactive<AttachedStoragePolicy[]>([]);
  const attached_policies_loading = reactive<LoadingStatus>({loading: false})

  // Load all Storage Policies of the team
  async function load_policies(with_bucket_count: boolean = true): Promise<null> {
    if(policies_loading.loading) return null

    policies_loading.loading = true
    return StoragePolicyService.list(with_bucket_count).then(({data}) => {
      data = data.map(policy => init_storage_policy(policy) as StoragePolicy)
      policies.splice(0)
      policies.push(...data)
    }).catch(({response}) => {
      console.error(`Error loading storage policies: HTTP ${response.status}`, response.body)
      ElMessage({
        type: "error",
        message: "Error loading Storage Policies"
      })
    }).finally(() => {policies_loading.loading = false})
  }

  // Load all Attached Storage Policies of the team
  async function load_all_attached(): Promise<null> {
    if(attached_policies_loading.loading) return null

    attached_policies_loading.loading = true
    return StoragePolicyService.list_all_attached().then(({data}) => {
      data = data.map(policy => init_storage_policy(policy) as AttachedStoragePolicy)
      attached_policies.splice(0)
      attached_policies.push(...data)
    }).catch(err => {
      console.error(`Error loading attached policies:`, err)
      ElMessage({
        type: "error",
        message: "Error loading Bucket Storage Policies"
      })
    }).finally(() => {attached_policies_loading.loading = false})
  }

  return {
    policies,
    policies_loading,
    load_policies,
    default_policy,

    load_all_attached,
    attached_policies,
    attached_policies_loading,
  };
});
